import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import Logo from '../common/Logo';
import '../styles/auth.css';

// LoginPage — v4 split-screen with illustrated hero + glass form.
// Register mode reuses the same shell; extra fields appear inline.
const LoginPage = () => {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [isRegister, setIsRegister] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [loginForm, setLoginForm] = useState({ schoolCode: '', email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({
    schoolName: '', schoolCode: '', email: '', password: '', firstName: '', lastName: ''
  });

  // Subdomain-aware tenant detection. If the user arrives on
  // `<code>.edu.rtconnect.in` (or ?tenant=CODE in dev), we fetch the school
  // info and lock the school-code field so the UI "feels" branded to that
  // tenant instead of asking them to re-enter their own code.
  const [tenant, setTenant] = useState(null);
  const [tenantLoaded, setTenantLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const override = params.get('tenant'); // dev convenience
        const { data } = await api.get('/auth/tenant', {
          params: override ? { tenant: override } : undefined,
          headers: override ? { 'X-Tenant-Code': override } : undefined
        });
        if (!cancelled && data?.tenant) {
          setTenant(data.tenant);
          setLoginForm((p) => ({ ...p, schoolCode: data.tenant.code }));
        }
      } catch {
        /* no tenant context — fall through to plain login */
      } finally {
        if (!cancelled) setTenantLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const onLoginChange = (e) => {
    setLoginForm((p) => ({ ...p, [e.target.name]: e.target.value }));
    setError('');
  };
  const onRegisterChange = (e) => {
    setRegisterForm((p) => ({ ...p, [e.target.name]: e.target.value }));
    setError('');
  };

  const submitLogin = async (e) => {
    e.preventDefault();
    // schoolCode is optional — leaving it blank routes to the platform-owner
    // flow (only superadmin accounts authenticate without a tenant code).
    if (!loginForm.email || !loginForm.password) {
      setError('Email and password are required.');
      return;
    }
    setLoading(true);
    try {
      const res = await login(loginForm.schoolCode, loginForm.email, loginForm.password);
      navigate(res?.user?.role === 'superadmin' ? '/platform' : '/dashboard');
    } catch (err) {
      const msg = err?.response?.data?.error?.message || err?.message || 'Login failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    const missing = Object.values(registerForm).some((v) => !v);
    if (missing) {
      setError('All fields are required.');
      return;
    }
    setLoading(true);
    try {
      await register(registerForm);
      navigate('/dashboard');
    } catch (err) {
      const msg = err?.response?.data?.error?.message || err?.message || 'Registration failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <aside className="login-hero">
        <div className="mesh" />
        <div className="grid-bg" />

        <Logo variant="emblem" size={56} showWordmark wordmarkSize={20} />

        <div className="illust-wrap">
          <svg className="illust" viewBox="0 0 520 380" fill="none">
            <ellipse cx="260" cy="340" rx="180" ry="16" fill="#000" opacity="0.4" />

            {/* Open book */}
            <g transform="translate(260, 210)">
              <ellipse cx="0" cy="60" rx="130" ry="8" fill="#000" opacity="0.4" />
              <path d="M -120 -40 Q -120 -50 -110 -50 L -5 -50 L -5 55 L -120 55 Z" fill="#fafaf9" stroke="#e7e5e4" strokeWidth="0.5" />
              <path d="M 120 -40 Q 120 -50 110 -50 L 5 -50 L 5 55 L 120 55 Z" fill="#fafaf9" stroke="#e7e5e4" strokeWidth="0.5" />
              <rect x="-5" y="-52" width="10" height="110" fill="#d48b13" rx="1" />
              {[-25, -15, -5, 5, 15, 25, 35].map((y, i) => (
                <React.Fragment key={i}>
                  <line x1="-100" y1={y} x2={-30 - (i % 3) * 10} y2={y} stroke={i === 0 ? '#78716c' : '#a8a29e'} strokeWidth={i === 0 ? 2 : 1.5} strokeLinecap="round" />
                  <line x1="20" y1={y} x2="100" y2={y} stroke={i === 0 ? '#78716c' : '#a8a29e'} strokeWidth={i === 0 ? 2 : 1.5} strokeLinecap="round" />
                </React.Fragment>
              ))}
              <path d="M 50 -50 L 50 -10 L 55 -20 L 60 -10 L 60 -50 Z" fill="#f43f5e" />
            </g>

            {/* Grad cap */}
            <g transform="translate(220, 30)" className="float1">
              <path d="M 0 15 L 40 0 L 80 15 L 40 30 Z" fill="#1c1a18" stroke="#2a2724" />
              <path d="M 15 25 L 15 40 Q 15 48 40 48 Q 65 48 65 40 L 65 25 L 40 35 Z" fill="#0a0a0a" stroke="#2a2724" />
              <line x1="70" y1="15" x2="70" y2="42" stroke="#f2ad1f" strokeWidth="1.5" />
              <circle cx="70" cy="44" r="3" fill="#f2ad1f" />
              <line x1="68" y1="46" x2="68" y2="54" stroke="#f2ad1f" strokeWidth="1" />
              <line x1="70" y1="46" x2="70" y2="55" stroke="#f2ad1f" strokeWidth="1" />
              <line x1="72" y1="46" x2="72" y2="54" stroke="#f2ad1f" strokeWidth="1" />
              <path d="M 10 14 L 40 3 L 45 5 L 18 17 Z" fill="#fff" opacity="0.15" />
            </g>

            {/* Pencil */}
            <g transform="translate(90, 120) rotate(-25)" className="float2">
              <rect x="0" y="0" width="10" height="70" fill="#f59e0b" rx="1" />
              <rect x="0" y="0" width="10" height="8" fill="#f43f5e" rx="1" />
              <rect x="0" y="8" width="10" height="3" fill="#9f5e3d" />
              <polygon points="0,70 10,70 5,80" fill="#fafaf9" />
              <polygon points="2,78 8,78 5,82" fill="#1c1a18" />
              <rect x="2" y="12" width="1.5" height="56" fill="#fff" opacity="0.3" />
            </g>

            {/* Ruler */}
            <g transform="translate(380, 80) rotate(15)" className="float3">
              <rect x="0" y="0" width="90" height="16" fill="#f2ad1f" rx="2" stroke="#d48b13" />
              {[10, 20, 30, 40, 50, 60, 70, 80].map((x, i) => (
                <line key={i} x1={x} y1="0" x2={x} y2={i % 2 === 0 ? 5 : 7} stroke="#09090b" strokeWidth="0.8" />
              ))}
            </g>

            {/* Calculator */}
            <g transform="translate(80, 260) rotate(-12)" className="float1">
              <rect x="0" y="0" width="50" height="70" rx="4" fill="#8b5cf6" stroke="#6d28d9" />
              <rect x="4" y="4" width="42" height="14" rx="2" fill="#0f172a" />
              <text x="42" y="14" fontFamily="monospace" fontSize="9" fill="#10b981" textAnchor="end">42</text>
              <g fill="#fafaf9" opacity="0.9">
                <rect x="5" y="22" width="8" height="8" rx="1.5" />
                <rect x="16" y="22" width="8" height="8" rx="1.5" />
                <rect x="27" y="22" width="8" height="8" rx="1.5" />
                <rect x="38" y="22" width="8" height="8" rx="1.5" fill="#f2ad1f" />
                <rect x="5" y="33" width="8" height="8" rx="1.5" />
                <rect x="16" y="33" width="8" height="8" rx="1.5" />
                <rect x="27" y="33" width="8" height="8" rx="1.5" />
                <rect x="38" y="33" width="8" height="8" rx="1.5" fill="#f2ad1f" />
                <rect x="5" y="44" width="8" height="8" rx="1.5" />
                <rect x="16" y="44" width="8" height="8" rx="1.5" />
                <rect x="27" y="44" width="8" height="8" rx="1.5" />
                <rect x="38" y="44" width="8" height="8" rx="1.5" fill="#f43f5e" />
                <rect x="5" y="55" width="19" height="8" rx="1.5" />
                <rect x="27" y="55" width="8" height="8" rx="1.5" />
                <rect x="38" y="55" width="8" height="8" rx="1.5" />
              </g>
            </g>

            {/* Apple */}
            <g transform="translate(420, 240)" className="float2">
              <path d="M 10 40 Q 0 30 5 18 Q 10 8 22 8 Q 30 8 35 14 Q 40 8 48 8 Q 60 8 65 18 Q 70 30 60 40 Q 55 50 45 52 Q 35 54 30 50 Q 25 54 15 52 Q 5 50 10 40 Z" fill="#f43f5e" />
              <path d="M 28 6 Q 30 0 36 0 L 38 2 L 35 6 Z" fill="#059669" />
              <path d="M 34 6 Q 40 -2 50 0 Q 45 4 36 10 Z" fill="#10b981" />
              <path d="M 12 18 Q 15 12 22 14" stroke="#fafaf9" strokeWidth="2" fill="none" opacity="0.5" strokeLinecap="round" />
            </g>

            {/* Math symbols */}
            <text x="160" y="50" fontFamily="serif" fontSize="26" fontWeight="700" fill="#f2ad1f" opacity="0.8" className="float2">π</text>
            <text x="340" y="170" fontFamily="serif" fontSize="22" fontWeight="700" fill="#8b5cf6" opacity="0.8" className="float1">∑</text>
            <text x="440" y="310" fontFamily="serif" fontSize="22" fontWeight="700" fill="#10b981" opacity="0.8" className="float3">∞</text>
            <text x="40" y="340" fontFamily="serif" fontSize="22" fontWeight="700" fill="#0ea5e9" opacity="0.8" className="float1">α</text>
            <text x="330" y="50" fontFamily="serif" fontSize="18" fontWeight="700" fill="#f43f5e" opacity="0.8" className="float3">√</text>

            {/* Sparkles */}
            <circle cx="70" cy="60" r="2" fill="#f2ad1f" />
            <circle cx="470" cy="120" r="1.5" fill="#f43f5e" />
            <circle cx="140" cy="200" r="1.5" fill="#8b5cf6" />
            <circle cx="410" cy="200" r="2" fill="#10b981" />
            <circle cx="200" cy="90" r="1.2" fill="#0ea5e9" />
            <circle cx="300" cy="310" r="1.8" fill="#f2ad1f" />
          </svg>
        </div>

        <div>
          <h1 className="hero-title">
            <span className="grad">One app.</span>
            <br />
            <span className="ital">Every</span> child.
          </h1>
          <p className="hero-desc">
            Admissions, attendance, marks, circulars, push notifications — all in one workspace,
            built for Indian schools.
          </p>
          <div className="hero-badges">
            <span className="hero-badge"><span className="dot-green" /> Live · 99.98%</span>
            <span className="hero-badge">🏫 500+ schools</span>
            <span className="hero-badge">🇮🇳 CBSE · ICSE · State</span>
          </div>
        </div>
      </aside>

      <div className="login-form-wrap">
        <div className="login-form-card">
          <h2 className="login-form-title">{isRegister ? 'Register your school' : 'Welcome back'}</h2>
          <p className="login-form-sub">
            {isRegister ? 'Set up your school workspace in one step.' : 'Sign in to continue to your workspace.'}
          </p>

          {error && <div className="login-error">{error}</div>}

          {!isRegister ? (
            <form onSubmit={submitLogin}>
              {tenant ? (
                <div className="login-tenant-chip" aria-label="Signed to school">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                    <path d="M12 2 3 7v7.5c0 4.5 3.5 8 9 8.5 5.5-.5 9-4 9-8.5V7z" />
                  </svg>
                  <div>
                    <div className="login-tenant-name">{tenant.name}</div>
                    <div className="login-tenant-code">Code · {tenant.code}</div>
                  </div>
                </div>
              ) : (
                <div className="login-field">
                  <label>School code <span style={{ color: 'var(--ink-3, #8a7560)', fontWeight: 400, fontSize: 12 }}>— leave blank if you're the platform owner</span></label>
                  <div className="input-wrap">
                    <svg className="icon-l" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                      <path d="M12 2 3 7v7.5c0 4.5 3.5 8 9 8.5 5.5-.5 9-4 9-8.5V7z" />
                    </svg>
                    <input name="schoolCode" placeholder="e.g. DEMO" value={loginForm.schoolCode} onChange={onLoginChange} disabled={loading || !tenantLoaded} autoComplete="off" />
                  </div>
                </div>
              )}
              <div className="login-field">
                <label>Email</label>
                <div className="input-wrap">
                  <svg className="icon-l" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                    <rect x="3" y="5" width="18" height="14" rx="2" />
                    <path d="m3 7 9 6 9-6" />
                  </svg>
                  <input type="email" name="email" placeholder="you@school.edu" value={loginForm.email} onChange={onLoginChange} disabled={loading} autoComplete="email" />
                </div>
              </div>
              <div className="login-field">
                <label>Password</label>
                <div className="input-wrap">
                  <svg className="icon-l" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                    <rect x="4" y="10" width="16" height="12" rx="2" />
                    <path d="M8 10V7a4 4 0 0 1 8 0v3" />
                  </svg>
                  <input type="password" name="password" placeholder="••••••••" value={loginForm.password} onChange={onLoginChange} disabled={loading} autoComplete="current-password" />
                </div>
              </div>
              <button type="submit" className="btn-login" disabled={loading}>
                {loading ? 'Signing in…' : 'Sign in'}
                {!loading && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <path d="M5 12h14" />
                    <path d="m12 5 7 7-7 7" />
                  </svg>
                )}
              </button>
              <p className="login-foot">
                New here? <button type="button" onClick={() => { setIsRegister(true); setError(''); }}>Register your school</button>
              </p>
              <p className="login-legal">
                <a href="/privacy">Privacy</a>
                <span aria-hidden>·</span>
                <a href="/terms">Terms</a>
                <span aria-hidden>·</span>
                <a href="/data-deletion">Data deletion</a>
              </p>
            </form>
          ) : (
            <form onSubmit={submitRegister}>
              <div className="login-field">
                <label>School name</label>
                <div className="input-wrap">
                  <svg className="icon-l" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M3 9 12 2l9 7" /><path d="M5 9v12h14V9" /></svg>
                  <input name="schoolName" placeholder="Springfield Public School" value={registerForm.schoolName} onChange={onRegisterChange} disabled={loading} />
                </div>
              </div>
              <div className="login-field">
                <label>School code</label>
                <div className="input-wrap">
                  <svg className="icon-l" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M12 2 3 7v7.5c0 4.5 3.5 8 9 8.5 5.5-.5 9-4 9-8.5V7z" /></svg>
                  <input name="schoolCode" placeholder="SPRING" value={registerForm.schoolCode} onChange={onRegisterChange} disabled={loading} autoComplete="off" />
                </div>
              </div>
              <div className="login-field">
                <label>Your email</label>
                <div className="input-wrap">
                  <svg className="icon-l" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>
                  <input type="email" name="email" placeholder="admin@school.edu" value={registerForm.email} onChange={onRegisterChange} disabled={loading} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="login-field">
                  <label>First name</label>
                  <div className="input-wrap">
                    <svg className="icon-l" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></svg>
                    <input name="firstName" value={registerForm.firstName} onChange={onRegisterChange} disabled={loading} />
                  </div>
                </div>
                <div className="login-field">
                  <label>Last name</label>
                  <div className="input-wrap">
                    <svg className="icon-l" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></svg>
                    <input name="lastName" value={registerForm.lastName} onChange={onRegisterChange} disabled={loading} />
                  </div>
                </div>
              </div>
              <div className="login-field">
                <label>Password</label>
                <div className="input-wrap">
                  <svg className="icon-l" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><rect x="4" y="10" width="16" height="12" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></svg>
                  <input type="password" name="password" placeholder="10+ chars, mixed case, digit, symbol" value={registerForm.password} onChange={onRegisterChange} disabled={loading} />
                </div>
              </div>
              <button type="submit" className="btn-login" disabled={loading}>
                {loading ? 'Creating…' : 'Create workspace'}
              </button>
              <p className="login-foot">
                Already have an account? <button type="button" onClick={() => { setIsRegister(false); setError(''); }}>Back to sign in</button>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
